// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { personSchema } from '@/account/api/types';
import { transportReferenceDetailsSchema } from '@/constants/shipping-carrier';
import {
  poLineItemStatusSchema,
  poStatusSchema,
} from '@/purchase-order/constants/status';
import {
  sparseCustomerSchema,
  sparseItemSchema,
  sparseLocationWithIntegrationSchema,
} from '@/sparse/api/types';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Schema for line item status.
 * Backend sends UPPERCASE, transforms to lowercase for frontend use.
 * Unknown statuses default to 'draft'.
 */

// =============================================================================
// Purchase Order Schemas
// =============================================================================

/**
 * Schema for a purchase order item.
 * Uses nested item structure where line item binding identifiers (id, key) are at the
 * top level, and catalog item properties are nested under `item`.
 */
const purchaseOrderItemSchema = z.object({
  // Order line item identifiers (binding id/key)
  id: z.string(),
  key: z.string(),
  // Nested catalog item properties
  item: sparseItemSchema,
  // Quantity fields
  requestedQuantity: z.number(),
  receivedQuantity: z.number(),
  openQuantity: z.number(),
  shippedQuantity: z.number(),
  deliveredQuantity: z.number(),
  status: poLineItemStatusSchema,
});

export type PurchaseOrderItem = z.infer<typeof purchaseOrderItemSchema>;

/**
 * Schema for a purchase order's 3PL receiving and sync state.
 */
const purchaseOrderThirdPartySchema = z.object({
  isEnabled: z.boolean(),
  // Hardfin's last poll of the 3PL for this purchase order
  lastSyncedAt: dateTimeSchema.nullable(),
  // The 3PL's last reported data update for this purchase order
  lastUpdateAt: dateTimeSchema.nullable(),
  // Whether the 3PL binding is currently in an error state
  hasSyncError: z.boolean(),
  syncErrorMessage: z.string().nullable(),
});

/**
 * Schema for a purchase order.
 */
export const purchaseOrderSchema = z.object({
  id: z.string(),
  key: z.string(),
  organizationId: z.string(),
  purchaseOrderNumber: z.string(),
  externalPurchaseOrderNumber: z.string().nullish(),
  supplier: sparseCustomerSchema,
  fromLocation: sparseLocationWithIntegrationSchema.nullable(),
  toLocation: sparseLocationWithIntegrationSchema,
  promiseShipDate: dateSchema.nullable(),
  promiseDeliveryDate: dateSchema.nullable(),
  status: poStatusSchema,
  thirdParty: purchaseOrderThirdPartySchema,
  orderNotes: z.string().nullable(),
  transportReferenceDetails: transportReferenceDetailsSchema.nullable(),
  items: z.array(purchaseOrderItemSchema),
  cancelledAt: dateTimeSchema.nullable(),
  cancelledBy: personSchema.nullable(),
  cancellationReason: z.string().nullable(),
  createdAt: dateTimeSchema,
  createdBy: personSchema,
  lastUpdatedAt: dateTimeSchema,
  lastUpdatedBy: personSchema,
  mostRecentReceivedAt: dateTimeSchema.nullable(),
  mostRecentDeliveredAt: dateTimeSchema.nullable(),
  mostRecentShippedAt: dateTimeSchema.nullable(),
});

export type PurchaseOrder = z.infer<typeof purchaseOrderSchema>;

/**
 * Schema for creating a purchase order item.
 */
const createPurchaseOrderItemSchema = z.object({
  itemId: z.string(),
  requestedQuantity: z.number().min(1),
});

/**
 * Schema for creating a purchase order.
 */
export const createPurchaseOrderSchema = z.object({
  supplierId: z.string(),
  fromLocationId: z.string().nullable(),
  toLocationId: z.string(),
  externalPurchaseOrderNumber: z.string().nullable(),
  promiseShipDate: dateSchema.nullable(),
  promiseDeliveryDate: dateSchema.nullable(),
  orderNotes: z.string().nullable(),
  transportReferenceDetails: transportReferenceDetailsSchema.nullable(),
  items: z.array(createPurchaseOrderItemSchema),
  fileIds: z.array(z.string()).nullable(),
});

export type CreatePurchaseOrderRequest = z.infer<
  typeof createPurchaseOrderSchema
>;

/**
 * Schema for updating a purchase order item.
 */
const updatePurchaseOrderItemSchema = z.object({
  itemId: z.string(),
  requestedQuantity: z.number().min(1).optional(),
});

/**
 * Schema for updating a purchase order.
 */
export const updatePurchaseOrderSchema = z.object({
  supplierId: z.string().optional(),
  fromLocationId: z.string().nullable().optional(),
  toLocationId: z.string().optional(),
  externalPurchaseOrderNumber: z.string().nullable().optional(),
  promiseShipDate: dateSchema.nullable().optional(),
  promiseDeliveryDate: dateSchema.nullable().optional(),
  orderNotes: z.string().nullable().optional(),
  transportReferenceDetails: transportReferenceDetailsSchema
    .nullable()
    .optional(),
  items: z.array(updatePurchaseOrderItemSchema).optional(),
});

export type UpdatePurchaseOrderRequest = z.infer<
  typeof updatePurchaseOrderSchema
>;
